import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { ThemeType, ThemeMode, IThemeConfig, DEFAULT_THEME_CONFIG, THEME_STORAGE_KEY } from "../model/theme.model";
import { ViewerConfigurationService } from "../../viewer-components/services/viewer-configuration.service";

/**
 * Service for managing Material 3 theme switching at runtime
 * Handles theme state, localStorage persistence, and DOM class manipulation
 */
@Injectable({
  providedIn: "root",
})
export class ThemeService {
  private themeConfigSubject: BehaviorSubject<IThemeConfig>;

  public themeConfig$: Observable<IThemeConfig>;

  constructor(public viewerConfiguration: ViewerConfigurationService) {
    // Initialize theme from localStorage or use default
    const initialConfig = this.loadThemeFromStorage();
    this.themeConfigSubject = new BehaviorSubject<IThemeConfig>(initialConfig);
    this.themeConfig$ = this.themeConfigSubject.asObservable();

    // Apply initial theme to DOM
    this.applyThemeToDom(initialConfig);
  }

  /**
   * Get the current theme configuration
   */
  getCurrentTheme(): IThemeConfig {
    return this.themeConfigSubject.value;
  }

  /**
   * Set the theme type (Brand, Generic A, Generic B)
   * @param themeType - The theme type to apply
   */
  setThemeType(themeType: ThemeType): void {
    const currentConfig = this.themeConfigSubject.value;
    const newConfig: IThemeConfig = {
      ...currentConfig,
      themeType,
    };
    this.updateTheme(newConfig);
  }

  /**
   * Set the theme mode (Light or Dark)
   * @param themeMode - The theme mode to apply
   */
  setThemeMode(themeMode: ThemeMode): void {
    const currentConfig = this.themeConfigSubject.value;
    const newConfig: IThemeConfig = {
      ...currentConfig,
      themeMode,
    };
    this.updateTheme(newConfig);
  }

  /**
   * Toggle between light and dark mode
   */
  toggleDarkMode(): void {
    const currentConfig = this.themeConfigSubject.value;
    const newMode = currentConfig.themeMode === ThemeMode.LIGHT ? ThemeMode.DARK : ThemeMode.LIGHT;
    this.setThemeMode(newMode);
  }

  /**
   * Update theme configuration and apply changes
   * @param config - The new theme configuration
   */
  private updateTheme(config: IThemeConfig): void {
    this.themeConfigSubject.next(config);
    this.saveThemeToStorage(config);
    this.applyThemeToDom(config);
  }

  /**
   * Apply theme classes to the HTML element
   * @param config - The theme configuration to apply
   */
  private applyThemeToDom(config: IThemeConfig): void {
    const htmlElement = document.documentElement;

    // Remove all existing theme classes
    this.removeAllThemeClasses(htmlElement);

    // Add new theme classes
    const themeClass = `theme-${config.themeType}`;
    htmlElement.classList.add(themeClass);

    // Add dark mode class if applicable
    if (config.themeMode === ThemeMode.DARK) {
      htmlElement.classList.add("dark-mode");
    }
  }

  /**
   * Remove all theme-related classes from an element
   * Removes any class that starts with 'theme-' and the 'dark-mode' class
   * @param element - The element to clean
   */
  private removeAllThemeClasses(element: HTMLElement): void {
    // Remove all classes that start with "theme-"
    Array.from(element.classList)
      .filter((className) => className.startsWith("theme-") || className === "dark-mode")
      .forEach((className) => element.classList.remove(className));
  }

  /**
   * Load theme configuration from localStorage
   * @returns The stored theme configuration or default
   */
  private loadThemeFromStorage(): IThemeConfig {
    try {
      const storedConfig = localStorage.getItem(this.storeKey());
      if (storedConfig) {
        const parsed = JSON.parse(storedConfig) as IThemeConfig;
        // Validate the parsed data
        if (this.isValidThemeConfig(parsed)) {
          return parsed;
        }
      }
    } catch (error) {
      console.warn("Failed to load theme from localStorage:", error);
    }

    return DEFAULT_THEME_CONFIG;
  }

  /**
   * Save theme configuration to localStorage
   * @param config - The theme configuration to save
   */
  private saveThemeToStorage(config: IThemeConfig): void {
    try {
      localStorage.setItem(this.storeKey(), JSON.stringify(config));
    } catch (error) {
      console.warn("Failed to save theme to localStorage:", error);
    }
  }

  private storeKey(): string {
    return `${this.viewerConfiguration.getViewerConfiguration()?.appName}` + "-" + THEME_STORAGE_KEY;
  }

  /**
   * Validate that a theme configuration is valid
   * @param config - The configuration to validate
   * @returns True if valid, false otherwise
   */
  private isValidThemeConfig(config: any): config is IThemeConfig {
    return (
      config &&
      typeof config === "object" &&
      Object.values(ThemeType).includes(config.themeType) &&
      Object.values(ThemeMode).includes(config.themeMode)
    );
  }
}
